<template>
  <PSelect
    :model-value="modelValue"
    :label="campo.label"
    :options="options"
    :multiple="isMultiple"
    :error="error"
    :show-error="Boolean(error)"
    :required="campo.obrigatorio"
    clearable
    filterable
    fit-input-width
    style="width: 100%"
    :placeholder="campo.placeholder ?? 'Selecione uma opção'"
    @change="handleChange"
  />
</template>

<script setup lang="ts">
import { PSelect } from "@f360-dev/f360-prisma";
import {
  TIntegracaoCampoModel,
  TIntegracaoCampoOpcao
} from "../../../@types/IntegracaoCampoModel";
import { computed, onMounted, ref, watch } from "vue";
import { ETipoVisualizacao } from "../../../@types/IntegracaoCampoEnum";
import handleEmpresasOpt from "@/core/utils/empresasOpt/handleEmpresasOpt";
import { useIntegracoesStore } from "@/features/financas/views/Integracoes/store/useIntegracoesStore";
import { diContainer } from "@/core/di";
import {
  CadastroDeIntegracaoDi,
  CadastroDeIntegracaoGateway
} from "../../../services/CadastroDeIntegracaoGateway";
import {
  ESelectFieldInitializationStrategy,
  resolveSelectFieldInitializationStrategy
} from "./selectFieldInitialization";

const props = defineProps<{
  campo: TIntegracaoCampoModel;
  modelValue: string | string[];
  error?: string;
  formValues?: Record<string, unknown>;
}>();

const options = ref<TIntegracaoCampoOpcao[]>([]);

const { handle: handleEmpresas } = handleEmpresasOpt();

const emit = defineEmits<{
  (e: "update:modelValue", value: string | string[]): void;
  (
    e: "update:selectedOptions",
    payload: { fieldName: string; options: TIntegracaoCampoOpcao[] }
  ): void;
}>();

const integracoesStore = useIntegracoesStore();
const cadastroGateway = diContainer.get<CadastroDeIntegracaoGateway>(
  CadastroDeIntegracaoDi
);

const isMultiple = computed(
  () =>
    (props.campo.tipo as unknown as ETipoVisualizacao) ===
    ETipoVisualizacao.SELECT_MULTIPLE
);

const handleChange = (value: string | string[]) => {
  if (isMultiple.value) {
    emit("update:modelValue", value);

    return;
  }

  const selectedValue = Array.isArray(value) ? value[0] : value;

  if (selectedValue && typeof selectedValue === "object") {
    const selectedRecord = selectedValue as Record<string, unknown>;
    const scalarValue =
      selectedRecord.value ??
      selectedRecord.Value ??
      selectedRecord._id ??
      selectedRecord.id ??
      selectedRecord.Id ??
      "";

    emit("update:modelValue", scalarValue == null ? "" : String(scalarValue));

    return;
  }

  emit("update:modelValue", selectedValue ?? "");
};

const endpointParameterEntries = computed(() =>
  Object.entries(props.campo.endpointParameters ?? {})
);

const endpointDependencyValues = computed(() =>
  endpointParameterEntries.value.map(([, parameter]) =>
    parameter.CampoId && props.formValues
      ? props.formValues[parameter.CampoId]
      : parameter.Valor
  )
);

const isCampoEmpresas = computed(() =>
  ["Empresa", "Empresas"].includes(props.campo.nomeObjetoNoLegado ?? "")
);

const handleEmpresasOptAsync = async () => {
  const empresas = await handleEmpresas();
  options.value = empresas;
};

const normalizeModelValues = (value: string | string[]): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (!value) return [];

  return [String(value)];
};

const emitSelectedOptions = (value: string | string[]) => {
  const modelValues = normalizeModelValues(value);
  const selectedOptions = options.value.filter((option) =>
    modelValues.includes(option.value)
  );

  emit("update:selectedOptions", {
    fieldName: props.campo.nomeCampo,
    options: selectedOptions
  });
};

const endpointWithParameters = (): string | null => {
  if (!props.campo.endpoint) return null;
  if (!endpointParameterEntries.value.length) return props.campo.endpoint;

  const urlSearchParams = new URLSearchParams();

  for (const [parameterName, parameter] of endpointParameterEntries.value) {
    const value =
      parameter.CampoId && props.formValues
        ? props.formValues[parameter.CampoId]
        : parameter.Valor;

    if (value === undefined || value === null || value === "") return null;

    urlSearchParams.set(parameterName, String(value));
  }

  const separator = props.campo.endpoint.includes("?") ? "&" : "?";

  return `${props.campo.endpoint}${separator}${urlSearchParams.toString()}`;
};

const handleEndpointOptionsAsync = async () => {
  const endpoint = endpointWithParameters();

  if (!endpoint) {
    options.value = [];

    return;
  }

  const response = await cadastroGateway.obterOpcoesPorEndpoint(endpoint);

  options.value = response?.Result ?? [];
  emitSelectedOptions(props.modelValue);
};

const loadStaticOptions = () => {
  options.value = (props.campo.opcoes ?? []).map((item) => ({
    label: item.label,
    value: item.value,
    attributes: item.attributes ?? null
  }));
};

const initializeOptionsAsync = async () => {
  const strategy = resolveSelectFieldInitializationStrategy(
    props.campo,
    endpointParameterEntries.value.length > 0,
    isCampoEmpresas.value
  );

  switch (strategy) {
    case ESelectFieldInitializationStrategy.Empresas:
      await handleEmpresasOptAsync();
      break;
    case ESelectFieldInitializationStrategy.FornecedoresConfiaveis:
      options.value = integracoesStore.listaFornecedoresConfiaveisOpt || [];
      break;
    case ESelectFieldInitializationStrategy.Endpoint:
      await handleEndpointOptionsAsync();
      break;
    case ESelectFieldInitializationStrategy.StaticOptions:
      loadStaticOptions();
      break;
    case ESelectFieldInitializationStrategy.DeferredEndpoint:
    default:
      break;
  }

  emitSelectedOptions(props.modelValue);
};

onMounted(async () => {
  await initializeOptionsAsync();
});

watch(
  endpointDependencyValues,
  async (_, oldValues) => {
    if (!endpointParameterEntries.value.length) return;

    if (oldValues) emit("update:modelValue", isMultiple.value ? [] : "");
    await handleEndpointOptionsAsync();
  },
  { immediate: true }
);

watch(
  () => props.modelValue,
  (value) => {
    emitSelectedOptions(value);
  },
  { deep: true }
);
</script>
